import { useState, useEffect } from 'react';
import axios from 'axios';
import { API, getAuthToken } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import FileUpload from '@/components/FileUpload';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const categories = ['Florals', 'Catering', 'Photography', 'Videography', 'Entertainment', 'Cakes & Desserts', 'Rentals', 'Venues', 'Other'];

const AdminVendors = () => {
  const [vendors, setVendors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({ name: '', category: 'Florals', description: '', logo_url: '', website: '', phone: '', email: '' });

  useEffect(() => { fetchVendors(); }, []);

  const fetchVendors = async () => {
    try {
      const res = await axios.get(`${API}/vendors`);
      setVendors(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const openDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({ name: item.name, category: item.category, description: item.description || '', logo_url: item.logo_url || '', website: item.website || '', phone: item.phone || '', email: item.email || '' });
    } else {
      setEditingItem(null);
      setFormData({ name: '', category: 'Florals', description: '', logo_url: '', website: '', phone: '', email: '' });
    }
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name) { toast.error('Name is required'); return; }
    setIsSaving(true);
    const token = getAuthToken();
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/vendors/${editingItem.id}`, formData, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/admin/vendors`, formData, { headers: { Authorization: `Bearer ${token}` } });
      }
      toast.success('Saved!');
      setIsDialogOpen(false);
      fetchVendors();
    } catch (error) {
      toast.error('Failed');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    const token = getAuthToken();
    try {
      await axios.delete(`${API}/admin/vendors/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted!');
      fetchVendors();
    } catch (error) {
      toast.error('Failed');
    }
  };

  if (isLoading) return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;

  return (
    <div data-testid="admin-vendors">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-3xl text-[#2C2C2C]">Preferred Vendors</h1>
        <Button onClick={() => openDialog()} className="bg-[#D4AF37] hover:bg-[#C5A028] text-white rounded-full">
          <Plus size={18} className="mr-2" /> Add Vendor
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {vendors.map((item, index) => (
          <div key={item.id} className="bg-white rounded-2xl shadow-soft p-6">
            <div className="flex items-start gap-4 mb-4">
              {item.logo_url ? (
                <img src={item.logo_url} alt={item.name} className="w-14 h-14 rounded-full object-cover" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-[#E8DFD0] flex items-center justify-center">
                  <span className="font-heading text-xl text-[#D4AF37]">{item.name.charAt(0)}</span>
                </div>
              )}
              <div>
                <h3 className="font-heading text-lg text-[#2C2C2C]">{item.name}</h3>
                <span className="text-sm text-[#D4AF37]">{item.category}</span>
              </div>
            </div>
            {item.description && <p className="text-[#6B7280] text-sm mb-4">{item.description}</p>}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => openDialog(item)} className="border-[#E8DFD0]"><Pencil size={14} className="mr-1" /> Edit</Button>
              <Button variant="outline" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500 border-red-200"><Trash2 size={14} className="mr-1" /> Delete</Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editingItem ? 'Edit' : 'Add'} Vendor</DialogTitle></DialogHeader>
          <div className="space-y-6 mt-4">
            <div><Label className="text-sm font-medium mb-2 block">Vendor Name *</Label><Input value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} className="input-elegant" /></div>
            <div><Label className="text-sm font-medium mb-2 block">Category</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData(p => ({ ...p, category: v }))}>
                <SelectTrigger className="input-elegant"><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-sm font-medium mb-2 block">Description</Label><Textarea value={formData.description} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} className="input-elegant" /></div>
            <div>
              <FileUpload currentUrl={formData.logo_url} onUpload={(url) => setFormData(p => ({ ...p, logo_url: url }))} accept="image/*" label="Upload Vendor Logo" />
              <div className="mt-2"><Label className="text-sm font-medium mb-2 block">Or paste Logo URL</Label><Input value={formData.logo_url} onChange={(e) => setFormData(p => ({ ...p, logo_url: e.target.value }))} className="input-elegant" /></div>
            </div>
            <div><Label className="text-sm font-medium mb-2 block">Website</Label><Input value={formData.website} onChange={(e) => setFormData(p => ({ ...p, website: e.target.value }))} className="input-elegant" /></div>
            <div><Label className="text-sm font-medium mb-2 block">Phone</Label><Input value={formData.phone} onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))} className="input-elegant" /></div>
            <div><Label className="text-sm font-medium mb-2 block">Email</Label><Input value={formData.email} onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))} className="input-elegant" /></div>
            <div className="flex gap-4 pt-4">
              <Button onClick={() => setIsDialogOpen(false)} variant="outline" className="flex-1">Cancel</Button>
              <Button onClick={handleSave} disabled={isSaving} className="flex-1 bg-[#D4AF37] hover:bg-[#C5A028] text-white">{isSaving ? 'Saving...' : 'Save'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminVendors;
